import styled from "styled-components";
import {H4, H5} from "../general/Headline";
import {useState} from "react";
import ToggleSwitch from "../ToggleSwitch";
import { Button } from "../general/buttons/Button";
import {setCookie} from "../../helpers/setCookie";
import {getCookie} from "../../helpers/getCookie";
import useScrollLock from "../../hooks/useScrollLock";
import {Overlay} from "../overlay/Overlay";
import {useTranslation} from 'react-i18next';
import {OverlayFooter} from "../overlay/OverlayFooter";
import {OverlayContent} from "../overlay/OverlayContent";

const MoreInfo = styled.b`
    &:hover {
        cursor: pointer;
    }

    display: block;
`;

const Buttons = styled.div`
    display: grid;
    flex-direction: column;
    grid-gap: 1.75rem;

    @media screen and (min-width: 768px) {
        grid-template-columns: 1fr 1fr;
        grid-gap: 1.375rem;
        flex-direction: row-reverse;
        flex-wrap: wrap;

        > button:nth-child(1) {
            grid-row: 1;
            grid-column: 2;
        }

        > button:nth-child(2) {
            grid-row: 1;
            grid-column: 1;
        }

        > button:nth-child(3) {
            grid-column: 1 / span 2;
        }
    }
`;

const ProviderList = styled.div`
    display: flex;
    flex-direction: column;
    grid-gap: 2rem;
`;

const Provider = styled.div``;

const ProviderCopy = styled.p`
    margin-bottom: 0;
`;

function CookieLayer() {
  const googleCookie = "Tracking-GoogleAnalytics";
  const googleAdwordsCookie = "Tracking-GoogleAdwords";
  const metaPixelCookie = "Tracking-MetaPixel";
  const [isOpen, setIsOpen] = useState( !getCookie( googleCookie ) );
  const [showProviderList, setShowProviderList] = useState( false );
  const [trackingConfig, setTrackingConfig] = useState( {
    googleAnalytics: false,
    googleAdwords: false,
    metaPixel: false,
  } );
  const {t} = useTranslation();

  function handleTrackingConfigChange( name, checked ) {
    setTrackingConfig( ( prevState ) => ( {...prevState, ...{[ name ]: checked}} ) );
  }

  function handleAcceptAll() {
    setCookie( googleCookie, "true", 730 );
    setCookie( googleAdwordsCookie, "true", 730 );
    setCookie( metaPixelCookie, "true", 730 );
    window.initGoogleTracking();
    window.initAdwordsTracking();
    setIsOpen( false );
  }

  function handleSave() {
    setCookie( googleCookie, trackingConfig.googleAnalytics, 730 );
    setCookie( googleAdwordsCookie, trackingConfig.googleAdwords, 730 );
    setCookie( metaPixelCookie, trackingConfig.metaPixel, 730 );
    if ( trackingConfig.googleAnalytics === true ) {
      window.initGoogleTracking();
    }
    if ( trackingConfig.googleAdwords === true ) {
      window.initAdwordsTracking();
    }
    if ( trackingConfig.metaPixel === true ) {
      window.initMetaTracking();
    }
    setIsOpen( false );
  }

  function handleDeclineAll() {
    setCookie( googleCookie, "false", 730 );
    setCookie( googleAdwordsCookie, "false", 730 );
    setCookie( metaPixelCookie, "false", 730 );
    setIsOpen( false );
  }

  if ( !isOpen ) return null;

  return (
    <Overlay>
      <OverlayContent>
        <H4 className="has-blue-700-color">{t( "cookieLayer.headline" )}</H4>
        <p>{t( "cookieLayer.copy" )}</p>
        {showProviderList ? (
          <ProviderList>
            <Provider>
              <ToggleSwitch
                defaultChecked={true}
                disabled={true}
                label={<H5 className="has-blue-700-color">{t( "cookieLayer.sections.functionality.headline" )}</H5>}
              />
              <ProviderCopy>{t( "cookieLayer.sections.functionality.description" )}</ProviderCopy>
            </Provider>
            <Provider>
              <ToggleSwitch
                defaultChecked={trackingConfig.googleAnalytics}
                name={"googleAnalytics"}
                label={<H5 className="has-blue-700-color">{t( "cookieLayer.sections.analytics.headline" )}</H5>}
                onChange={( event ) => {
                  const name = event.target.name;
                  const checked = event.target.checked;
                  handleTrackingConfigChange( name, checked );
                }}
              />
              <ProviderCopy>{t( "cookieLayer.sections.analytics.description" )}</ProviderCopy>
            </Provider>
            <Provider>
              <ToggleSwitch
                defaultChecked={trackingConfig.googleAdwords}
                name={"googleAdwords"}
                label={<H5 className="has-blue-700-color">{t( "cookieLayer.sections.personalizedAds.headline" )}</H5>}
                onChange={( event ) => {
                  const name = event.target.name;
                  const checked = event.target.checked;
                  handleTrackingConfigChange( name, checked );
                }}
              />
              <ProviderCopy>{t( "cookieLayer.sections.personalizedAds.description" )}</ProviderCopy>
            </Provider>
            <Provider>
              <ToggleSwitch
                defaultChecked={trackingConfig.metaPixel}
                name={"metaPixel"}
                label={<H5 className="has-blue-700-color">{t( "cookieLayer.sections.metaPixel.headline" )}</H5>}
                onChange={( event ) => {
                  const name = event.target.name;
                  const checked = event.target.checked;
                  handleTrackingConfigChange( name, checked );
                }}
              />
              <ProviderCopy>{t( "cookieLayer.sections.metaPixel.description" )}</ProviderCopy>
            </Provider>
          </ProviderList>
        ) : (
          <MoreInfo onClick={() => setShowProviderList( true )}>{t( "cookieLayer.actions.moreInfo" )}</MoreInfo>
        )}
      </OverlayContent>
      <OverlayFooter>
        <Buttons>
          <Button color={"yellow"} type={"primary"} onClick={handleAcceptAll}>
            {t( "cookieLayer.actions.accept" )}
          </Button>
          <Button color={"blue"} type={"primary"} onClick={handleDeclineAll}>
            {t( "cookieLayer.actions.reject" )}
          </Button>
          {showProviderList && (
            <Button color={"blue"} type={"secondary"} onClick={handleSave}>
              {t( "cookieLayer.actions.save" )}
            </Button>
          )}
        </Buttons>
      </OverlayFooter>
    </Overlay>
  );
}

export default CookieLayer;
